/* ============================================================
   c_quantity-stepper.js
   ────────────────────────────────────────────────────────────
   上下△で数量を増減するステッパー。

   DOM 契約:
     [data-c-qty]              … ルート
       [data-c-qty-input]      … input[type=number][name=quantity]
       [data-c-qty-step="1"]   … 上△（button[type=button]）
       [data-c-qty-step="-1"]  … 下△

   - 上限は input の max 属性（属性が無ければ上限なし）、下限は min（既定 1）
   - 上限超え・空欄・非数値は無音でクランプする（トースト等は出さない）
   - 端に達した△は disabled ではなく aria-disabled="true" にする。
     disabled にするとフォーカス中の△（Space 連打で上限到達など）から
     フォーカスが body へ飛び、次の Tab がページ先頭に戻ってしまうため。
   - max 属性による制約検証（ブラウザ標準のツールチップ）が発火しないよう、
     入力途中でも上限だけは即座にクランプする。下限・空欄は入力を妨げないよう
     change / blur まで待って確定させる。送信時にも最終クランプする
     （Enter による暗黙送信などで change が飛ばない経路の保険）。

   max 属性を動的に書き換えたとき（バリアント切替など）は
   `document.dispatchEvent(new CustomEvent('c:qty:refresh'))` で再同期する。

   スタイルは assets/c_main-product.css の `.c-pdp-qty__*` に置いている。
   他セクションで使う場合はスタイルの移設が必要。
   ============================================================ */
(function () {
  'use strict';

  // セクションが同一ページに 2 つ配置されると同じ <script src> が 2 回実行され、
  // document への委譲リスナーが二重登録されて 1 クリックで +2 になる。
  if (window.__cQtyStepperInit) return;
  window.__cQtyStepperInit = true;

  function getInput(root) {
    return root.querySelector('[data-c-qty-input]');
  }

  function getBounds(input) {
    var min = parseInt(input.getAttribute('min'), 10);
    var max = parseInt(input.getAttribute('max'), 10);
    return {
      min: isNaN(min) ? 1 : min,
      max: isNaN(max) ? null : max
    };
  }

  function clamp(value, bounds) {
    if (isNaN(value)) return bounds.min;
    if (value < bounds.min) return bounds.min;
    if (bounds.max !== null && value > bounds.max) return bounds.max;
    return value;
  }

  /* 現在値で押せなくなった△に aria-disabled を立てる（フォーカスは保つ） */
  function syncButtons(root, input) {
    var bounds = getBounds(input);
    var current = parseInt(input.value, 10);
    if (isNaN(current)) current = bounds.min;
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-c-qty-step]'),
      function (btn) {
        var step = parseInt(btn.getAttribute('data-c-qty-step'), 10);
        if (isNaN(step)) return;
        var next = current + step;
        var blocked = next < bounds.min || (bounds.max !== null && next > bounds.max);
        btn.setAttribute('aria-disabled', blocked ? 'true' : 'false');
      }
    );
  }

  /* 値を書き換えたことは画面には出さない（無音クランプ）。
     ただし支援技術には伝えないと何が起きたか分からないため、
     視覚非表示の live region にだけ上限を流す。 */
  /* 前回予約した再通知は必ず捨てる。残すと、クランプが解消した後に古いタイマーが
     発火して「クランプしていないのに Maximum が読まれる」（素早い入力で起きる）。 */
  function cancelAnnounce(el) {
    if (el && el.cQtyAnnounceTimer) {
      window.clearTimeout(el.cQtyAnnounceTimer);
      el.cQtyAnnounceTimer = null;
    }
  }

  function clearAnnounce(root) {
    var el = root.querySelector('[data-c-qty-status]');
    if (!el) return;
    cancelAnnounce(el);
    if (el.textContent !== '') el.textContent = '';
  }

  function announceClamp(root, bounds, requested) {
    var el = root.querySelector('[data-c-qty-status]');
    if (!el) return;
    cancelAnnounce(el);
    var text = '';
    if (!isNaN(requested) && bounds.max !== null && requested > bounds.max) {
      text = 'Maximum ' + bounds.max + '.';
    }
    if (el.textContent === text) {
      // 同じ文面をそのまま入れ直しても DOM が変化せず読み上げられない。
      // 2 回目以降のクランプが無言にならないよう、一度空にしてから入れ直す。
      // （空文字への更新なら何も通知されないので、その場合は素通り）
      if (text === '') return;
      el.textContent = '';
      el.cQtyAnnounceTimer = window.setTimeout(function () {
        el.cQtyAnnounceTimer = null;
        el.textContent = text;
      }, 100);
      return;
    }
    el.textContent = text;
  }

  /* silent: 上限が便宜値のとき（full 状態の max="1"）に「Maximum 1.」という
     無意味な通知が流れるのを防ぐ。full では入力欄自体を隠すので、
     数量の上限を語る文脈ではない。 */
  /* JS で値を書き換えたことを、同じフォームを読むほかのスクリプトに知らせる。
     input.value の代入ではイベントが出ないため、エクスプレスチェックアウト
     （Shopify の payment_button）が change で数量を覚える実装だと、
     △で増やした数・上限に丸めた数が決済に反映されない。
     自分の onCommit も change を聞いているので、出している間は無視させる。 */
  function notifyChanged(input) {
    input.cQtyNotifying = true;
    try {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } finally {
      input.cQtyNotifying = false;
    }
  }

  function setValue(root, input, value, silent) {
    var bounds = getBounds(input);
    var next = String(clamp(value, bounds));
    if (input.value !== next) {
      input.value = next;
      notifyChanged(input);
    }
    syncButtons(root, input);
    if (silent) clearAnnounce(root);
    else announceClamp(root, bounds, value);
  }

  function onClick(e) {
    if (!e.target || !e.target.closest) return;
    var btn = e.target.closest('[data-c-qty-step]');
    if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
    var root = btn.closest('[data-c-qty]');
    if (!root) return;
    var input = getInput(root);
    if (!input) return;
    var step = parseInt(btn.getAttribute('data-c-qty-step'), 10);
    if (isNaN(step)) return;
    var current = parseInt(input.value, 10);
    if (isNaN(current)) {
      // 空欄から△を押したときは min へ寄せる（min + step にすると
      // 上△ 1 回で 2 になってしまうため）
      setValue(root, input, getBounds(input).min);
      return;
    }
    setValue(root, input, current + step);
  }

  /* 入力途中: 上限超えだけ即クランプ（制約検証のツールチップを出さないため） */
  function onInput(e) {
    var input = e.target;
    if (!input.matches || !input.matches('[data-c-qty-input]')) return;
    var root = input.closest('[data-c-qty]');
    if (!root) return;
    var bounds = getBounds(input);
    var value = parseInt(input.value, 10);
    if (bounds.max !== null && !isNaN(value) && value > bounds.max) {
      input.value = String(bounds.max);
      notifyChanged(input);
    }
    syncButtons(root, input);
    announceClamp(root, bounds, value);
  }

  /* 確定時: 空欄・非数値・下限割れも含めて全クランプ */
  function onCommit(e) {
    var input = e.target;
    if (!input.matches || !input.matches('[data-c-qty-input]')) return;
    if (input.cQtyNotifying) return;
    var root = input.closest('[data-c-qty]');
    if (!root) return;
    setValue(root, input, parseInt(input.value, 10));
  }

  /* ── カート状態への追随 ──────────────────────────────────
     Liquid が焼き込む「カート内数量」はページ生成時点のスナップショットで、
     全ページに描画されるカートドロワーから数量を変更されると古くなる。
     放置すると (a) 上限を超えて追加できてしまい (b) 上限到達で無効化した
     CTA が、ドロワーから削除しても無効のまま復帰しなくなる。
     Dawn の cartUpdate（assets/cart.js / product-form.js が publish）と
     bfcache 復帰を拾って /cart.js から引き直す。 */

  function cartQuantityOf(cart, variantId) {
    var items = (cart && cart.items) || [];
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].variant_id) === String(variantId)) {
        total += items[i].quantity;
      }
    }
    return total;
  }

  function applyCartState(root, inCart) {
    var input = getInput(root);
    if (!input) return;

    var note = root.querySelector('[data-c-qty-note]');
    if (note) {
      var tpl = root.getAttribute('data-c-qty-note-template') || '';
      if (inCart > 0 && tpl) {
        // Liquid の replace は全件置換なので、こちらも全件置換で揃える
        // （String#replace は文字列指定だと最初の 1 件しか置き換えない）
        note.textContent = tpl.split('__COUNT__').join(String(inCart));
        note.hidden = false;
      } else {
        note.hidden = true;
      }
    }

    var limit = parseInt(root.getAttribute('data-c-qty-limit'), 10);
    var unlimited = isNaN(limit) || limit <= 0;
    var remaining = unlimited ? null : limit - inCart;
    var full = remaining !== null && remaining <= 0;

    if (unlimited) {
      input.removeAttribute('max');
    } else {
      // full のときの 1 は便宜値。max="0" にすると value="1" が制約検証に
      // 引っかかってブラウザのツールチップが出る。この状態では入力欄自体を
      // 隠して CTA も無効化するので、値そのものは使われない。
      input.setAttribute('max', String(full ? 1 : remaining));
    }

    var control = root.querySelector('[data-c-qty-control]');
    if (control) control.hidden = full;

    var form = root.closest('form');
    var cta = form && form.querySelector('[data-c-qty-cta]');
    if (cta) {
      // ラベルは必ず属性から取る。現在の textContent から推測すると、
      // 上限到達状態で描画されたページを復帰させたときに
      // 「Maximum in Cart」を通常ラベルとして覚えてしまう。
      var label = cta.getAttribute(
        full ? 'data-c-qty-full-label' : 'data-c-qty-base-label'
      );
      // 属性が無い（テーマ側の記述漏れ）なら推測せずラベルは触らない
      if (label && (cta.textContent || '').trim() !== label) {
        cta.textContent = label;
      }
      cta.disabled = full;
    }

    // エクスプレスチェックアウトも上限到達時は出さない。
    // カートとは別に決済されるため、出したままだと受注品の残枠を超えて買えてしまう
    // （sections/c_main-product.liquid の該当コメント参照）。
    var express = form && form.querySelector('[data-c-express]');
    if (express) express.hidden = full;

    // full のときの max は便宜値なので、その値でのクランプは通知しない
    setValue(root, input, parseInt(input.value, 10), full);
  }

  function cartUrl() {
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'cart.js';
  }

  /* ドロワーの数量連打で fetch が並行するため、最新の応答だけを反映する */
  var refreshToken = 0;

  /* 完了を待てるよう Promise を返す（テストと将来の呼び出し側のため） */
  function refreshFromCart() {
    var roots = document.querySelectorAll('[data-c-qty][data-c-qty-variant]');
    if (!roots.length || typeof fetch !== 'function') return Promise.resolve();
    var token = ++refreshToken;
    return fetch(cartUrl(), { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        // 後着した古い応答は捨てる（捨てないと古い状態で上書きされる）
        if (!cart || token !== refreshToken) return;
        Array.prototype.forEach.call(roots, function (root) {
          applyCartState(root, cartQuantityOf(cart, root.getAttribute('data-c-qty-variant')));
        });
      })
      .catch(function () {
        // 取得できないときはサーバー描画の状態を維持する（誤って開放しない）
      });
  }

  /* 対象範囲のステッパーを現在の min/max で再確定する */
  function commitAll(scope) {
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-c-qty]'),
      function (root) {
        var input = getInput(root);
        if (input) setValue(root, input, parseInt(input.value, 10));
      }
    );
  }

  /* 送信直前の保険。Enter による暗黙送信などで change が飛ばない経路をここで塞ぐ。
     submit ハンドラ内での value 変更は送信データに反映される。 */
  function onSubmit(e) {
    var form = e.target;
    if (!form || form.nodeName !== 'FORM') return;
    commitAll(form);
  }

  function init() {
    commitAll(document);
    document.addEventListener('click', onClick);
    document.addEventListener('input', onInput);
    document.addEventListener('change', onCommit);
    // blur は capture で拾う（フォーカスアウト系イベントはバブルしないため）
    document.addEventListener('blur', onCommit, true);
    document.addEventListener('submit', onSubmit, true);
    // max 属性を書き換えた側（バリアント切替など）からの再同期用
    document.addEventListener('c:qty:refresh', function () {
      commitAll(document);
    });

    // ドロワー等でカートが変わったら残数を引き直す（assets/pubsub.js は
    // theme.liquid の head で読み込み済みだが、無い環境でも壊さない）
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refreshFromCart);
    }
    // カート追加後にブラウザバックで bfcache から復元された場合
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) refreshFromCart();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
